import { HelpMedia } from "./helpMedia";
import {
  Alert,
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CloseIcon } from "components/ui/icons";
import React, { useState } from "react";

interface HelpVideoDialogProps {
  title: string;
  media: HelpMedia;
  onClose: () => void;
  onPlay: () => void;
  onEnded: () => void;
}

/**
 * The large player. It starts with sound: it only ever opens from a press on
 * play, and that press is the user gesture browsers require. Captions are on
 * by default.
 *
 * Full screen on phones. On a short screen (a phone held sideways) the title
 * bar goes too and the close button floats over the video, so the video gets
 * the whole height.
 */
const HelpVideoDialog = ({
  title,
  media,
  onClose,
  onPlay,
  onEnded,
}: HelpVideoDialogProps) => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("sm"));
  const short = useMediaQuery("(max-height: 500px)");
  const [failed, setFailed] = useState(false);

  const closeButton = (
    <IconButton
      aria-label="Close video"
      onClick={onClose}
      sx={
        short
          ? {
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              color: "#fff",
              bgcolor: "rgba(0, 0, 0, 0.5)",
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
            }
          : { position: "absolute", right: 8, top: 8 }
      }
    >
      <CloseIcon />
    </IconButton>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={narrow || short}
      // Without the title bar the title still names the dialog.
      PaperProps={short ? { "aria-label": title } : undefined}
    >
      {short ? (
        closeButton
      ) : (
        <DialogTitle sx={{ pr: 14 }}>
          {title}
          {closeButton}
        </DialogTitle>
      )}
      <Box
        sx={{
          bgcolor: "#000",
          lineHeight: 0,
          ...(short && { flex: 1, minHeight: 0 }),
        }}
      >
        {failed ? (
          <Alert severity="info" sx={{ m: 4 }}>
            {"This video isn't available right now."}
          </Alert>
        ) : (
          <video
            data-testid="help-video"
            controls
            autoPlay
            playsInline
            poster={media.poster}
            src={media.video}
            onPlay={onPlay}
            onEnded={onEnded}
            onError={() => setFailed(true)}
            style={
              short
                ? {
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                  }
                : { width: "100%", maxHeight: "80vh", display: "block" }
            }
          >
            <track
              kind="captions"
              src={media.captions}
              srcLang="en"
              label="English"
              default
            />
          </video>
        )}
      </Box>
    </Dialog>
  );
};

export default HelpVideoDialog;
